import React, { useState, useRef } from 'react';
import { Search, ChevronDown, ChevronUp, Upload, ClipboardList, Snowflake, MapPin, MessageCircleQuestion, AlertTriangle, Wrench } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Card, PageHeader, TabBar, ProgressBar, Badge } from '../components/Card';

const ALL_TABS = [
  { id: 'upload', label: 'Upload Report', icon: Upload },
  { id: 'main', label: 'Main Checklist', icon: ClipboardList },
  { id: 'seasonal', label: 'Seasonal', icon: Snowflake },
  { id: 'ottawa', label: 'Ottawa Specifics', icon: MapPin, ottawaOnly: true },
  { id: 'questions', label: 'Questions', icon: MessageCircleQuestion },
  { id: 'redflags', label: 'Red Flags', icon: AlertTriangle },
  { id: 'costs', label: 'Cost Estimates', icon: Wrench },
];

const STATUS_OPTIONS = [
  { value: 'good', label: 'Good', color: 'var(--green)' },
  { value: 'fair', label: 'Fair', color: 'var(--yellow)' },
  { value: 'poor', label: 'Poor', color: 'var(--red)' },
  { value: 'na', label: 'N/A', color: 'var(--gray-500)' },
];

const STATIC_RED_FLAGS = [
  { flag: 'Foundation Cracks', severity: 'Critical', desc: 'Horizontal or stair-step cracks in foundation walls may indicate structural issues.' },
  { flag: 'Active Water Intrusion', severity: 'Critical', desc: 'Water stains, mold, or active leaks in basement or around windows.' },
  { flag: 'Knob & Tube Wiring', severity: 'Major', desc: 'Old electrical wiring may not be insurable and needs replacement.' },
  { flag: 'Vermiculite Insulation', severity: 'Major', desc: 'May contain asbestos. Common in homes built before 1990.' },
  { flag: 'Roof Near End of Life', severity: 'Major', desc: 'Budget $8,000-$15,000 for roof replacement.' },
  { flag: 'Galvanized Steel Plumbing', severity: 'Moderate', desc: 'Corrodes from inside out. May need replacement within 5-10 years.' },
];

export default function Inspection() {
  const [activeTab, setActiveTab] = useState('main');
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const fileInputRef = useRef(null);
  const { inspectionItems, updateInspectionItem, getInspectionStats, getPoorItems, seasonalChecks, setSeasonalChecks, ottawaChecks, setOttawaChecks, settings } = useApp();
  const stats = getInspectionStats();
  const pct = stats.total > 0 ? Math.round((stats.reviewed / stats.total) * 100) : 0;
  const poorItems = getPoorItems();
  const isOttawa = settings.isOttawa === true;
  const TABS = ALL_TABS.filter(t => !t.ottawaOnly || isOttawa);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const toggleSeasonalCheck = (key) => {
    setSeasonalChecks(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleOttawaCheck = (key) => {
    setOttawaChecks(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div>
      <PageHeader
        icon={<Search size={22} />}
        title="Home Inspection"
        subtitle="Complete your inspection checklist and track issues"
      />

      <TabBar tabs={TABS} activeTab={activeTab} onTabChange={setActiveTab} />

      {activeTab === 'upload' && (
        <Card style={{ textAlign: 'center', padding: 48 }}>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            style={{ display: 'none' }}
          />
          {!uploadedFile ? (
            <>
              <Upload size={48} color="var(--gray-400)" style={{ marginBottom: 16 }} />
              <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Upload Your Inspection Report</h3>
              <p style={{ color: 'var(--gray-500)', marginBottom: 20 }}>
                Upload a PDF or image of your home inspection report for reference
              </p>
              <button onClick={() => fileInputRef.current.click()} style={{
                padding: '12px 24px', background: 'var(--primary)', color: 'white',
                borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 14, cursor: 'pointer',
              }}>
                Choose File
              </button>
            </>
          ) : (
            <>
              <div style={{
                width: 64, height: 64, borderRadius: 'var(--radius-sm)',
                background: 'var(--green)', color: 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px',
              }}>
                <ClipboardList size={32} />
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Report Uploaded</h3>
              <p style={{ color: 'var(--gray-500)', marginBottom: 4 }}>{uploadedFile.name}</p>
              <p style={{ color: 'var(--gray-400)', fontSize: 13, marginBottom: 20 }}>
                {(uploadedFile.size / 1024).toFixed(1)} KB
              </p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                <button onClick={() => fileInputRef.current.click()} style={{
                  padding: '10px 20px', background: 'var(--primary)', color: 'white',
                  borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 14, cursor: 'pointer',
                }}>
                  Replace File
                </button>
                <button onClick={() => setUploadedFile(null)} style={{
                  padding: '10px 20px', background: 'var(--gray-200)',
                  borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 14, cursor: 'pointer',
                }}>
                  Remove
                </button>
              </div>
            </>
          )}
        </Card>
      )}

      {activeTab === 'main' && (
        <div>
          <Card style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Inspection Progress</h3>
            <ProgressBar value={stats.reviewed} max={stats.total} color="var(--green)" height={10} />
            <div style={{ fontSize: 14, color: 'var(--gray-600)', marginTop: 8 }}>
              {stats.reviewed} of {stats.total} items reviewed ({pct}%)
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
              {STATUS_OPTIONS.map(s => (
                <Badge key={s.value} color={s.color}>
                  {s.value === 'good' ? '✓' : s.value === 'fair' ? '—' : s.value === 'poor' ? '⚠' : '⊘'} {s.label}: {stats[s.value] || 0}
                </Badge>
              ))}
            </div>
          </Card>

          {inspectionItems.map((category, catIdx) => {
            const catReviewed = category.items.filter(i => i.status !== null).length;
            const isExpanded = expandedCategory === catIdx;
            return (
              <Card key={catIdx} style={{ marginBottom: 8, padding: 0, overflow: 'hidden' }}>
                <button
                  onClick={() => setExpandedCategory(isExpanded ? null : catIdx)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', padding: '16px 20px',
                    background: 'none', fontSize: 14, fontWeight: 600,
                    textTransform: 'uppercase', letterSpacing: '0.5px',
                  }}
                >
                  <span>{category.name}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontWeight: 400, color: 'var(--gray-500)' }}>
                      {catReviewed}/{category.items.length}
                    </span>
                    {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </div>
                </button>
                {isExpanded && (
                  <div style={{ padding: '0 20px 16px' }}>
                    {category.items.map((item, itemIdx) => (
                      <div key={item.id} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '12px 0', borderTop: '1px solid var(--gray-100)',
                      }}>
                        <span style={{ fontSize: 14 }}>{item.label}</span>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {STATUS_OPTIONS.map(opt => (
                            <button
                              key={opt.value}
                              onClick={() => updateInspectionItem(catIdx, itemIdx, opt.value)}
                              style={{
                                padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500,
                                background: item.status === opt.value ? opt.color : 'var(--gray-100)',
                                color: item.status === opt.value ? 'white' : 'var(--gray-600)',
                                transition: 'all 0.15s',
                              }}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {activeTab === 'seasonal' && (
        <Card>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Seasonal Considerations</h3>
          {[
            { season: 'Winter', items: ['Check heating system efficiency', 'Look for ice dams on roof', 'Check insulation in attic', 'Test all windows for drafts'] },
            { season: 'Spring', items: ['Check basement for water intrusion', 'Inspect sump pump operation', 'Look for grading issues', 'Check roof for winter damage'] },
            { season: 'Summer', items: ['Test AC system thoroughly', 'Check for pest evidence', 'Inspect deck/patio condition', 'Review landscaping drainage'] },
            { season: 'Fall', items: ['Inspect furnace before winter', 'Check chimney and fireplace', 'Clean gutters and downspouts', 'Seal windows and doors'] },
          ].map(s => (
            <div key={s.season} style={{ marginBottom: 20 }}>
              <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8, color: 'var(--primary)' }}>{s.season}</h4>
              {s.items.map((item, i) => {
                const key = `${s.season}-${i}`;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--gray-100)' }}>
                    <input type="checkbox" checked={!!seasonalChecks[key]} onChange={() => toggleSeasonalCheck(key)} style={{ width: 18, height: 18 }} />
                    <span style={{ fontSize: 14 }}>{item}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </Card>
      )}

      {activeTab === 'ottawa' && (
        <Card>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Ottawa-Specific Inspection Items</h3>
          {[
            { category: 'Winter Readiness', items: ['Furnace capacity for -30°C weather', 'Window rating (Energy Star for Zone 2)', 'Pipe insulation in exterior walls', 'Snow load capacity of roof'] },
            { category: 'Ottawa Soil & Foundation', items: ['Leda clay awareness (expansive soil)', 'Basement waterproofing system', 'Foundation crack monitoring', 'Sump pump with battery backup'] },
            { category: 'Municipal Services', items: ['Lead pipe replacement status', 'Sewer line condition', 'Water meter and shutoff', 'Hydro Ottawa service panel'] },
          ].map(s => (
            <div key={s.category} style={{ marginBottom: 20 }}>
              <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8, color: 'var(--primary)' }}>{s.category}</h4>
              {s.items.map((item, i) => {
                const key = `${s.category}-${i}`;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--gray-100)' }}>
                    <input type="checkbox" checked={!!ottawaChecks[key]} onChange={() => toggleOttawaCheck(key)} style={{ width: 18, height: 18 }} />
                    <span style={{ fontSize: 14 }}>{item}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </Card>
      )}

      {activeTab === 'questions' && (
        <Card>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Questions to Ask Your Inspector</h3>
          {[
            'What are the most urgent repairs needed?',
            'Is there any evidence of water damage or mold?',
            'How old is the roof, and how many years of life does it have left?',
            'Are there any safety concerns (electrical, structural)?',
            'What maintenance should I prioritize in the first year?',
            'Are there any signs of pest infestation?',
            'Is the foundation in good condition?',
            'How is the overall drainage around the property?',
            'Are there any building code violations?',
            'What are the biggest long-term expenses I should plan for?',
          ].map((q, i) => (
            <div key={i} style={{ padding: '12px 0', borderBottom: '1px solid var(--gray-100)', display: 'flex', gap: 12 }}>
              <span style={{ color: 'var(--primary)', fontWeight: 600, fontSize: 14, flexShrink: 0, width: 24, textAlign: 'right' }}>{i + 1}.</span>
              <span style={{ fontSize: 14 }}>{q}</span>
            </div>
          ))}
        </Card>
      )}

      {activeTab === 'redflags' && (
        <div>
          {poorItems.length > 0 && (
            <Card style={{ marginBottom: 20 }}>
              <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>Your Flagged Issues</h3>
              <p style={{ color: 'var(--gray-500)', fontSize: 14, marginBottom: 20 }}>
                Items you've marked as "Poor" in the checklist
              </p>
              {poorItems.map((item, i) => (
                <div key={i} style={{
                  padding: 16, marginBottom: 12, borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--red)',
                  borderLeft: '4px solid var(--red)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontWeight: 600 }}>{item.label}</span>
                    <Badge color="var(--red)">Poor</Badge>
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--gray-600)' }}>Category: {item.category}</p>
                </div>
              ))}
            </Card>
          )}

          {poorItems.length === 0 && (
            <Card style={{ marginBottom: 20, textAlign: 'center', padding: 32 }}>
              <AlertTriangle size={48} color="var(--gray-300)" style={{ marginBottom: 12 }} />
              <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 6 }}>
                No issues flagged yet!
              </h3>
              <p style={{ color: 'var(--gray-400)', fontSize: 14 }}>
                Items you mark as "Poor" in the Main Checklist will appear here.
              </p>
            </Card>
          )}

          <Card>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>Common Red Flags to Watch For</h3>
            <p style={{ color: 'var(--gray-500)', fontSize: 14, marginBottom: 20 }}>
              Issues that may require immediate attention or negotiation
            </p>
            {STATIC_RED_FLAGS.map((item, i) => (
              <div key={i} style={{
                padding: 16, marginBottom: 12, borderRadius: 'var(--radius-sm)',
                border: `1px solid ${item.severity === 'Critical' ? 'var(--red)' : item.severity === 'Major' ? 'var(--yellow)' : 'var(--gray-300)'}`,
                borderLeft: `4px solid ${item.severity === 'Critical' ? 'var(--red)' : item.severity === 'Major' ? 'var(--yellow)' : 'var(--gray-400)'}`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontWeight: 600 }}>{item.flag}</span>
                  <Badge color={item.severity === 'Critical' ? 'var(--red)' : item.severity === 'Major' ? 'var(--yellow)' : 'var(--gray-500)'}>
                    {item.severity}
                  </Badge>
                </div>
                <p style={{ fontSize: 13, color: 'var(--gray-600)' }}>{item.desc}</p>
              </div>
            ))}
          </Card>
        </div>
      )}

      {activeTab === 'costs' && (
        <Card>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Typical Repair Cost Estimates</h3>
          <p style={{ color: 'var(--gray-500)', fontSize: 14, marginBottom: 20 }}>
            {isOttawa ? 'Ottawa-area' : 'Canadian average'} pricing for common home repairs (2026 estimates)
          </p>
          {[
            { item: 'Roof Replacement', range: '$8,000 - $15,000' },
            { item: 'Furnace Replacement', range: '$4,000 - $7,000' },
            { item: 'AC Unit Replacement', range: '$3,500 - $6,000' },
            { item: 'Water Heater', range: '$1,200 - $2,500' },
            { item: 'Electrical Panel Upgrade', range: '$2,000 - $4,000' },
            { item: 'Foundation Repair', range: '$5,000 - $30,000+' },
            { item: 'Plumbing Re-pipe', range: '$4,000 - $10,000' },
            { item: 'Window Replacement (per)', range: '$500 - $1,200' },
            { item: 'Bathroom Renovation', range: '$8,000 - $25,000' },
            { item: 'Kitchen Renovation', range: '$15,000 - $50,000' },
          ].map((item, i) => (
            <div key={i} style={{
              display: 'flex', justifyContent: 'space-between', padding: '12px 0',
              borderBottom: '1px solid var(--gray-100)', fontSize: 14,
            }}>
              <span style={{ fontWeight: 500 }}>{item.item}</span>
              <span style={{ color: 'var(--gray-600)' }}>{item.range}</span>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
